import { useEffect, useState } from "react";
import { OverviewDetailsProps } from "../Overview/interfaces/interface";
import useUserStore from "../../../services/userStore";
import { myLocalStorage } from "../../StorageHelper";
import TenantLists from "../TenantLists";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Button } from "../../ui/button";
import { RefreshCw } from "lucide-react";
import { AlertWhitelistPayloadItem, getAlertsData, submitWhitelistedAlerts } from "./Api/alertsapi";
import { AlertData, AlertLevelData, AlertsTimelineData, RiskyAlertData, RiskyMachineData, RiskyUserData } from "../interfaces/CommonInterfaces";
import { getAlertLevels, getAlertsTimeline, getRiskyAlerts, getRiskyMachines, getRiskyUsers, transformAlertsResponse } from "./helper";
import AlertOverview from "./AlertsOverview";
import AlertLevel from "./AlertLevel";
import AlertRecentData from "./AlertRecentData";
import RiskyAlerts from "../../../pages/dashboards/Alerts/RiskyAlerts";
import OverviewRiskyAlerts from "./OverviewRiskyAlerts";
import OverviewRiskyUsers from "./OverviewRiskyUsers";
import OverviewRiskyMachines from "./OverviewRiskyMachines";

export default function AlertDetails({ onSelectorChange, fromdetails = false, computerName = "", computeremail = "", computerId = "" }: OverviewDetailsProps) {
  const [selectedTime, setSelectedTime] = useState<string>("lw");
  const [selectedTenant, setSelectedTenant] = useState(
    myLocalStorage.getItem("latestTenant")?.tenantName || ""
  );

  const { email } = useUserStore((state) => state.user);
  const tenantName = selectedTenant || myLocalStorage.getItem("latestTenant")?.tenantName || "";

  const [alerts, setAlerts] = useState<AlertData[]>([]);
  const [levelData, setLevelData] = useState<AlertLevelData[]>([]);
  const [timelineData, setTimelineData] = useState<AlertsTimelineData[]>([]);
  const [riskyAlerts, setRiskyAlerts] = useState<RiskyAlertData[]>([]);
  const [riskyUsers, setRiskyUsers] = useState<RiskyUserData[]>([]);
  const [riskyMachines, setRiskyMachines] = useState<RiskyMachineData[]>([]);
  const [loading, setLoading] = useState(false);

  const handleWhitelistSelected = async (selectedAlerts: AlertData[]) => {
    console.log("Selected alerts for whitelisting:", selectedAlerts);
    const normalizedPayload: AlertWhitelistPayloadItem[] = selectedAlerts.map((alert) => {
      const processValue = alert.Process || alert.processName || "";
      return {
        message: alert.message || "",
        computerId: alert.computerId || computerId || "",
        computerName: alert.computerName || "",
        process: processValue,
        processUser: alert.user || "",
        level: alert.level || "",
        enabled: true,
      };
    });

    await submitWhitelistedAlerts(normalizedPayload, tenantName);
  };

  const fetchAlertsData = async () => {
    if (!tenantName || !email) {
      return;
    }

    setLoading(true);
    let key = "alertsKey";
    try {
      const response = await getAlertsData(
        email,
        tenantName,
        "ondevice-auditlog-alerts",
        selectedTime,
        computerName,
        computerId,
        computeremail,
        key,
        fromdetails
      );

      if (response) {
        const sortedResponse = (response as any[]).sort(
          (a, b) => new Date(b._time).getTime() - new Date(a._time).getTime()
        );
        const alerts: AlertData[] = transformAlertsResponse(sortedResponse as any[]);
        setAlerts(alerts);
        setLevelData(getAlertLevels(alerts));
        setTimelineData(getAlertsTimeline(alerts));
        setRiskyAlerts(getRiskyAlerts(alerts));
        setRiskyUsers(getRiskyUsers(alerts));
        setRiskyMachines(getRiskyMachines(alerts));



      } else {
        console.log("No data received");
      }
    } catch (error) {
      console.error("Error fetching overview data:", error);
    }
    setLoading(false);
  }

  const handleRefresh = () => {
    void fetchAlertsData();
  };

  useEffect(() => {
    void fetchAlertsData();
  }, [selectedTime, tenantName, email, computerName, computerId, computeremail, fromdetails]);

  return (
    <>
      <div className="p-6 space-y-6">
        {/* Tenant selector */}
        {!fromdetails ? (
          <div className="flex justify-between items-center pb-4 border-b">
            {/* Tenant List on the left */}
            <div className="flex-1">
              <TenantLists
                onSelectorChange={(value: string) => {
                  setSelectedTenant(value);
                  onSelectorChange?.(value);
                }}
                isLabelNeeded={true}
              />
            </div>

            {/* Time Range and Refresh on the right */}
            <div className="flex items-center gap-2">
              <div className="relative">
                <label
                  htmlFor="time-range-selector"
                  className="absolute -top-4 left-3 z-10 px-1 text-xs font-medium text-muted-foreground"
                >
                  Time Range
                </label>
                <Select value={selectedTime} onValueChange={setSelectedTime}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="24Hrs">Last 24 Hours</SelectItem>
                    <SelectItem value="lw">Last 7 Days</SelectItem>
                    <SelectItem value="lm">Last 30 Days</SelectItem>
                    <SelectItem value="l3m">Last 90 Days</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
                onClick={handleRefresh}
                disabled={loading}
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </Button>
            </div>
          </div>
        ) : <div className="flex items-center justify-between gap-2">
          <div className="relative">
            <label
              htmlFor="time-range-selector"
              className="absolute -top-4 left-3 z-10 px-1 text-xs font-medium text-muted-foreground"
            >
              Time Range
            </label>
            <Select value={selectedTime} onValueChange={setSelectedTime}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="24Hrs">Last 24 Hours</SelectItem>
                <SelectItem value="lw">Last 7 Days</SelectItem>
                <SelectItem value="lm">Last 30 Days</SelectItem>
                <SelectItem value="l3m">Last 90 Days</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
            onClick={handleRefresh}
            disabled={loading}
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>}

        {/* Overview Cards */}
        <div className="grid items-stretch grid-cols-1 gap-6 lg:grid-cols-3">
          <AlertOverview alertsline={timelineData} loading={loading} />
          <AlertLevel leveldata={levelData} loading={loading} />
        </div>

        <AlertRecentData
          alerts={alerts}
          loading={loading}
          tenantName={tenantName}
          onWhitelistSelected={handleWhitelistSelected}
          fromDetails={fromdetails}
          computerId={computerId}
          computeremail={computeremail}
        />

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <OverviewRiskyAlerts riskyAlerts={riskyAlerts} loading={loading} />
          <OverviewRiskyUsers riskyUsers={riskyUsers} loading={loading} />
          <OverviewRiskyMachines riskyMachines={riskyMachines} loading={loading} />
        </div>
      </div>

    </>
  )
}
